import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';
import { useInView } from '../hooks/useInView';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

/* ==================================================================
   Every component here follows the same contract:
   content renders visible, and motion is only ever *added* on top.
   Under prefers-reduced-motion the animation layer is skipped entirely.
   ================================================================== */

type RevealDir = 'up' | 'left' | 'right' | 'scale';

export function Reveal({
  as: Tag = 'div',
  dir = 'up',
  delay = 0,
  className = '',
  children,
}: {
  as?: ElementType;
  dir?: RevealDir;
  delay?: number;
  className?: string;
  children: ReactNode;
}) {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>();
  const active = inView && !reduced;

  return (
    <Tag
      ref={ref}
      className={className}
      data-reveal={active ? 'shown' : undefined}
      data-reveal-dir={dir === 'up' ? undefined : dir}
      style={active && delay ? ({ animationDelay: `${delay}ms` } as CSSProperties) : undefined}
    >
      {children}
    </Tag>
  );
}

/**
 * Word-by-word headline reveal. The text stays in the DOM as one accessible
 * string — each word is wrapped in an aria-hidden-free inline span, so screen
 * readers and copy-paste still get a normal sentence.
 */
export function SplitText({
  text,
  as: Tag = 'h2',
  className = '',
  delay = 0,
}: {
  text: string;
  as?: ElementType;
  className?: string;
  delay?: number;
}) {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLHeadingElement>({ threshold: 0.2 });
  const active = inView && !reduced;
  const words = text.split(' ');

  return (
    <Tag ref={ref} className={className} data-split={active ? 'shown' : undefined}>
      {words.map((word, i) => (
        // The separator is a text node *between* the spans, not inside them:
        // trailing whitespace within an inline-block is trimmed, which would
        // run the words together.
        <Fragment key={`${word}-${i}`}>
          <span
            data-word=""
            style={{ '--word-index': i, animationDelay: `${delay}ms` } as CSSProperties}
          >
            {word}
          </span>
          {i < words.length - 1 ? ' ' : ''}
        </Fragment>
      ))}
    </Tag>
  );
}

/** Counts up to a target once visible. The final value is the initial render
    value under reduced motion, so the figure is never wrong or missing. */
export function Counter({
  value,
  prefix = '',
  suffix = '',
  duration = 1600,
  className = '',
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLSpanElement>({ threshold: 0.4 });
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    if (!inView || reduced) return;

    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      // easeOutExpo — fast start, long settle
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      setDisplay(Math.round(value * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };

    setDisplay(0);
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, reduced, value, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display.toLocaleString('en-US')}
      {suffix}
    </span>
  );
}

/** Adds data-grow="shown" when visible, which drives the bar/line grow-in
    keyframes declared in index.css. */
export function GrowOnView({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.3 });

  return (
    <div ref={ref} className={className} data-grow={inView && !reduced ? 'shown' : undefined}>
      {children}
    </div>
  );
}

/** Pointer-driven 3D tilt. Pointer-coarse devices and reduced-motion users get
    a plain static card — no hover dependency anywhere. */
export function TiltCard({
  className = '',
  max = 6,
  children,
}: {
  className?: string;
  max?: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node || reduced) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    let frame = 0;
    const onMove = (event: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = node.getBoundingClientRect();
        const px = (event.clientX - rect.left) / rect.width - 0.5;
        const py = (event.clientY - rect.top) / rect.height - 0.5;
        node.style.setProperty('--tilt-y', `${px * max * 2}deg`);
        node.style.setProperty('--tilt-x', `${-py * max * 2}deg`);
      });
    };
    const reset = () => {
      cancelAnimationFrame(frame);
      node.style.setProperty('--tilt-x', '0deg');
      node.style.setProperty('--tilt-y', '0deg');
    };

    node.addEventListener('pointermove', onMove);
    node.addEventListener('pointerleave', reset);
    return () => {
      cancelAnimationFrame(frame);
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerleave', reset);
    };
  }, [max, reduced]);

  return (
    <div ref={ref} className={`tilt ${className}`}>
      {children}
    </div>
  );
}

/**
 * Scroll-linked parallax. Uses a rAF-throttled scroll listener rather than a
 * listener per frame, and only runs while the element is on screen.
 */
export function Parallax({
  speed = 0.15,
  className = '',
  children,
}: {
  speed?: number;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  const { ref: sentinel, inView } = useInView<HTMLDivElement>({ once: false, threshold: 0 });

  useEffect(() => {
    const node = ref.current;
    if (!node || reduced || !inView) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * -speed;
      node.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [reduced, speed, inView]);

  return (
    <div ref={sentinel} className={className}>
      <div ref={ref} className="will-change-transform">
        {children}
      </div>
    </div>
  );
}

/**
 * Draws SVG strokes on first view. The dash is applied by CSS *only* while the
 * accompanying animation runs, so an unsupported browser, a failed measurement
 * or a skipped effect all leave the artwork drawn rather than invisible.
 */
export function DrawOnView({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.25 });
  const [run, setRun] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || reduced || !inView) return;

    node.querySelectorAll<SVGGeometryElement>('[data-draw]').forEach((shape, i) => {
      const length = typeof shape.getTotalLength === 'function' ? shape.getTotalLength() : 0;
      if (length > 0) shape.style.setProperty('--draw-len', `${Math.ceil(length)}`);
      shape.style.setProperty('--draw-index', `${i}`);
    });

    setRun(true);
  }, [inView, reduced, ref]);

  return (
    <div ref={ref} className={className} data-draw-state={run ? 'run' : undefined}>
      {children}
    </div>
  );
}
